/**
 * BalanceFallRiskCard — равновесие, координация и риск от падане (Nerve2).
 * Ромберг с отворени/затворени очи разделя сетивната от малкомозъчната
 * нестабилност; тремор в покой със скованост насочва към базалните ганглии.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Activity, AlertTriangle, Loader2, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RISK_COLOR = {
  low: "text-emerald-500", moderate: "text-amber-500",
  high: "text-orange-500", very_high: "text-rose-500",
};

export const BalanceFallRiskCard = ({ d, setD, cardCls, border }) => {
  const proto = d?.pathway_protocols?.motor;
  const res = d?.motor_balance;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    romberg_open: 30, romberg_closed: 30, single_leg: 10, tandem_steps: 10,
    finger_nose: "ok", tremor: "none", stiffness: "none",
    falls_6m: 0, near_falls: false,
  });

  if (!proto) return null;

  const num = (k, v, max) => setF((p) => ({
    ...p, [k]: Math.max(0, Math.min(max, parseInt(v || 0, 10) || 0)),
  }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/neuro-twin/motor-exam`, f);
      setD(data);
      setOpen(false);
      toast.success("Тестът за равновесие е записан");
    } catch { toast.error("Записът не мина"); }
    finally { setBusy(false); }
  };

  return (
    <div className={cardCls} data-testid="neuro-balance-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold inline-flex items-center gap-1.5">
            <Activity className="w-4 h-4 text-violet-500" />
            Равновесие, координация и риск от падане
          </div>
          {res ? (
            <>
              <p className={`text-[12px] font-bold mt-1 ${RISK_COLOR[res.risk]}`}
                data-testid="neuro-balance-risk">
                {res.risk_bg} · {res.score}/100 ({res.date})
              </p>
              <div className="mt-2 space-y-1.5">
                {res.ataxia.kinds.map((k) => (
                  <div key={k.key} className={`p-2 rounded-lg border ${border}`}
                    data-testid={`neuro-ataxia-${k.key}`}>
                    <div className="text-[12px] font-bold">{k.title_bg}</div>
                    <div className="text-[11px] opacity-80 mt-0.5">{k.text_bg}</div>
                    <div className="text-[11px] font-medium mt-0.5">{k.action_bg}</div>
                  </div>
                ))}
              </div>
              {res.drivers?.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="neuro-balance-drivers">
                  {res.drivers.map((x, i) => (
                    <li key={i} className="text-[11px]">
                      <span className="font-bold">{x.label_bg}</span>
                      <span className="opacity-80"> — {x.text_bg}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <p className="text-[12px] opacity-75 mt-1">
              Още няма тест. Ромберг с отворени и със затворени очи, стоеж на
              един крак и тандемна походка — 3 минути, и се вижда дали
              нестабилността е от липсващ сетивен вход или от координацията.
            </p>
          )}
        </div>
        <button type="button" onClick={() => setOpen((v) => !v)}
          className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold shrink-0"
          data-testid="neuro-balance-toggle">
          {open ? "Затвори" : res ? "Нов тест" : "Направи теста"}
        </button>
      </div>

      {open && (
        <div className="mt-3" data-testid="neuro-balance-form">
          <div className="p-2 rounded-lg border border-amber-500/40 bg-amber-500/10"
            data-testid="neuro-balance-safety">
            <div className="text-[12px] font-bold inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
              <ShieldAlert className="w-3.5 h-3.5" /> Преди да станете
            </div>
            <ul className="mt-1 space-y-0.5 list-disc pl-4">
              {proto.safety_bg.map((s, i) => (
                <li key={i} className="text-[11px] opacity-90">{s}</li>
              ))}
            </ul>
          </div>

          <div className="mt-3 space-y-2">
            {proto.tests.map((t) => (
              <div key={t.key} className={`p-2 rounded-lg border ${border}`}>
                <div className="text-[12px] font-bold">{t.label_bg}</div>
                <div className="text-[10px] opacity-70 mt-0.5">{t.how_bg}</div>
                <input type="number" min={0} max={t.key === "tandem_steps" ? 10 : 30}
                  value={f[t.key]}
                  onChange={(e) => num(t.key, e.target.value, t.key === "tandem_steps" ? 10 : 30)}
                  className={`mt-1.5 w-24 px-2 py-1 rounded-md border bg-transparent text-[12px] ${border}`}
                  data-testid={`neuro-motor-${t.key}`} />
              </div>
            ))}

            {[["finger_nose", "Пръст-нос", proto.finger_nose],
              ["tremor", "Тремор", proto.tremor],
              ["stiffness", "Скованост и тонус", proto.stiffness]].map(([key, label, opts]) => (
              <div key={key} className={`p-2 rounded-lg border ${border}`}>
                <div className="text-[12px] font-bold">{label}</div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {opts.map((o) => (
                    <button key={o.key} type="button"
                      onClick={() => setF((p) => ({ ...p, [key]: o.key }))}
                      className={`px-2 py-1 rounded-full text-[11px] font-semibold border ${
                        f[key] === o.key ? "border-violet-500/50 bg-violet-500/15" : border}`}
                      data-testid={`neuro-motor-${key}-${o.key}`}>
                      {o.label_bg}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className={`p-2 rounded-lg border ${border}`}>
              <div className="text-[12px] font-bold">Падания през последните 6 месеца</div>
              <input type="number" min={0} max={50} value={f.falls_6m}
                onChange={(e) => num("falls_6m", e.target.value, 50)}
                className={`mt-1.5 w-24 px-2 py-1 rounded-md border bg-transparent text-[12px] ${border}`}
                data-testid="neuro-motor-falls" />
              <button type="button"
                onClick={() => setF((p) => ({ ...p, near_falls: !p.near_falls }))}
                className={`mt-2 block px-2 py-1 rounded-full text-[11px] font-semibold border ${
                  f.near_falls ? "border-amber-500/50 bg-amber-500/15" : border}`}
                data-testid="neuro-motor-near-falls">
                {f.near_falls ? "Имам почти-падания (задържал съм се)" : "Без почти-падания"}
              </button>
            </div>
          </div>

          <button type="button" onClick={save} disabled={busy}
            className="mt-3 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="neuro-balance-save">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Запиши теста
          </button>
        </div>
      )}

      {res?.meds_bg?.length > 0 && (
        <p className="text-[11px] mt-2 inline-flex items-start gap-1.5 text-amber-600 dark:text-amber-400"
          data-testid="neuro-balance-meds">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          Лекарства от списъка Ви, които увеличават риска: {res.meds_bg.join(", ")}.
          Не се спират сами — говори се с лекаря за доза и час.
        </p>
      )}
      {res?.note_bg && <p className="text-[10px] opacity-50 mt-2">{res.note_bg}</p>}
    </div>
  );
};

export default BalanceFallRiskCard;
